import { createContext, useContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { userApi } from '../api/userApi'
import type { UserPreferences, RelationshipType } from '../types/api'

interface UserPreferencesContextType {
  preferences: UserPreferences | null
  relationshipType: RelationshipType
  loading: boolean
  refreshPreferences: () => Promise<void>
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined)

const CACHE_KEY = 'user_preferences_cache'
const CACHE_TIMESTAMP_KEY = 'user_preferences_cache_timestamp'
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Load preferences from localStorage cache
 */
function loadFromCache(): UserPreferences | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY)
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY)
    
    if (!cached || !timestamp) {
      return null
    }
    
    const age = Date.now() - parseInt(timestamp, 10)
    if (age > CACHE_DURATION) {
      // Cache expired
      localStorage.removeItem(CACHE_KEY)
      localStorage.removeItem(CACHE_TIMESTAMP_KEY)
      return null
    }
    
    return JSON.parse(cached) as UserPreferences
  } catch (error) {
    console.error('[UserPreferences] Error loading from cache:', error)
    return null
  }
}

/**
 * Save preferences to localStorage cache
 */
function saveToCache(preferences: UserPreferences): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(preferences))
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString())
  } catch (error) {
    console.error('[UserPreferences] Error saving to cache:', error)
  }
}

/**
 * Clear preferences cache
 */
function clearCache(): void {
  localStorage.removeItem(CACHE_KEY)
  localStorage.removeItem(CACHE_TIMESTAMP_KEY)
}

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null)
  const [loading, setLoading] = useState(true)

  // Fixed relationship type (provider type removed from preferences)
  const relationshipType: RelationshipType = 'COACH_TRAINEE'

  /**
   * Fetch preferences from API and update cache
   */
  const fetchPreferences = async () => {
    try {
      console.log('[UserPreferences] Fetching from API...')
      const data = await userApi.getUserPreferences()
      console.log('[UserPreferences] Fetched:', data)
      
      setPreferences(data)
      saveToCache(data)
      
      return data
    } catch (error) {
      console.log('[UserPreferences] Error fetching preferences:', error)
      // If error, keep using cached/default preferences
      return null
    }
  }

  /**
   * Refresh preferences (called after user updates preferences)
   */
  const refreshPreferences = async () => {
    console.log('[UserPreferences] Refreshing preferences...')
    await fetchPreferences()
  }

  // Load preferences on mount
  useEffect(() => {
    let cancelled = false

    async function loadPreferences() {
      // Try to load from cache first
      const cached = loadFromCache()
      
      if (cached) {
        console.log('[UserPreferences] Loaded from cache:', cached)
        setPreferences(cached)
        setLoading(false)
        
        // Fetch fresh data in background to update cache
        if (!cancelled) {
          fetchPreferences()
        }
      } else {
        // No cache, fetch from API
        await fetchPreferences()
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadPreferences()

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <UserPreferencesContext.Provider
      value={{
        preferences,
        relationshipType,
        loading,
        refreshPreferences,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  )
}

/**
 * Hook to access user preferences
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useUserPreferences() {
  const context = useContext(UserPreferencesContext)
  
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider')
  }
  
  return context
}

/**
 * Clear preferences cache (useful for logout)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function clearPreferencesCache() {
  clearCache()
}
